/**
 * RoMSH 입력 기록(romsh_input_v1·v2) 런타임 검사·상태 판별·후보 연결 — PLAN 2-9a PR-3.
 *
 * 계약: docs/2026/260923_RoMSH_입력기록_기획.md §2(저장 모양)·§3-4(상태 판별).
 * v2(TIC 높이 비율, PLAN 2-9d): docs/2026/260930_RoMSH_TIC높이_구현계약.md.
 * 문구·칸은 저장된 기록의 schema·abundance_source 로만 고른다(romshInputSource).
 *
 * - 기록은 `unknown` 으로 받는다. 필수 키·열거값을 검사한 뒤에만 타입을 좁힌다
 *   (프론트에 ErrorBoundary 가 없어 렌더 예외가 나면 대시보드 전체가 빈다).
 * - `model_received_status = ok` 인 기록의 되돌림 검사는 백엔드 판정 규칙(§2-6 ③)과
 *   같다 — 백엔드가 ok 로 저장한 기록을 형식 불량으로 보이지 않는다.
 * - 값을 다시 계산하지 않는다. 표시는 기록된 값을 옮겨 적는다(§2-8 1).
 */
import {
  ROMSH_INPUT_EVIDENCE_TEXT as EV_TEXT,
  ROMSH_INPUT_HEIGHT_TEXT as HEIGHT_TEXT,
  ROMSH_INPUT_NEUTRAL_TEXT as NEUTRAL_TEXT,
  ROMSH_INPUT_STATE_TEXT as STATE_TEXT,
  ROMSH_INPUT_TEXT as TEXT,
} from "@/constants/romshInputText";
import type {
  AnalysisEvidence,
  AnalysisFeature,
  RomshInputComponent,
  RomshInputComponentV2,
  RomshInputRecord,
  RomshInputV1,
  RomshInputV2,
} from "@/types";

/** 프론트는 지금까지 나온 모든 schema 를 계속 그린다(§2-7). */
export const KNOWN_ROMSH_INPUT_SCHEMAS = [
  "romsh_input_v1",
  "romsh_input_v2",
] as const;

/** v2 알려진 출처(TIC 높이 비율)의 고정값 — 계약 「입력 기록과 화면」. */
const HEIGHT_SOURCE = "tic_height_fraction";
const HEIGHT_RULE = "v3.tic-height";
const HEIGHT_WINNER_RULE = "scan_top1_smiles_max_height";
const HEIGHT_DEFINITION_STATUS = "user_approved_hypothesis";
/** 보낸 성분 비율 합이 1 인지 볼 때의 허용 오차(기록 반올림 여유). */
const FRACTION_SUM_TOLERANCE = 1e-3;
/** TIC 연결 허용 오차 — 계약 4 의 절대 1e-9분뿐(backend romsh_abundance.TIC_RT_TOLERANCE_MIN). */
const TIC_RT_TOLERANCE_MIN = 1e-9;

const RECORD_KEYS = [
  "schema",
  "outcome",
  "selection",
  "request",
  "components",
  "ai_response",
  "model_received_status",
  "model_received_note",
  "model_received",
  "model_received_raw",
  "model_output",
  "failure",
  "record_error",
] as const;

const OUTCOMES = ["sent", "not_sent", "transport_error"];
const REASONS = [
  "all_within_limit",
  "abundance_reserve",
  "rt_bin_fill",
  "remaining_fill",
];
const RT_SOURCES = ["scan", "missing_sent_as_zero"];
/** v2 는 단일 RT 가 확인된 스캔을 scan_single_rt 로 적는다. */
const HEIGHT_RT_SOURCE = "scan_single_rt";
const RT_SOURCES_V2 = [...RT_SOURCES, HEIGHT_RT_SOURCE];
/** 선택 단계 멈춤 가운데 기존처럼 아무것도 그리지 않는 건너뜀 코드. */
const SELECT_SKIP_CODES = ["no_winners", "no_smiles"];
const WINNER_RULES = ["per_scan", "legacy", null];
const RECEIVED = [
  "ok",
  "absent",
  "error",
  "invalid",
  "mismatch",
  "not_applicable",
];
const STAGES = [
  "select",
  "build",
  "circuit",
  "transport",
  "ai_http",
  "client_parse",
  "ai",
];

/** 백엔드 쪽 계약 상수와 같다(romsh_input_record.ECHO_META_NAMES). */
export const ECHO_META_NAMES = [
  "fuel_proxy",
  "mix_norm",
  "ab_entropy_norm",
  "dominant_abundance",
  "rt_span_norm",
] as const;
const ECHO_KEYS = [
  "echo_version",
  "components",
  "meta",
  "fuel_proxy_source",
  "fuel_proxy_method",
  "fuel_proxy_basis",
  "min_carbon_tokens",
  "rt_span_p95",
  "max_mols",
  "env_name",
  "env_id",
  "env_name_known",
  "threshold_source",
  "model_sha256",
  "vocab_sha256",
  "torch_version",
];
const ECHO_COMPONENT_KEYS = [
  "position",
  "smiles",
  "retention_time",
  "abundance",
  "hydrocarbon_rule",
  "carbon_tokens",
  "hetero_tokens",
  "unknown_tokens",
  "truncated",
];
const NON_FINITE_STRINGS = ["NaN", "Infinity", "-Infinity"];

// ---------------------------------------------------------------------------
// 형 검사 도우미
// ---------------------------------------------------------------------------

type Obj = Record<string, unknown>;

function isObj(x: unknown): x is Obj {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}
function isInt(x: unknown): x is number {
  return typeof x === "number" && Number.isInteger(x);
}
function isNonNegInt(x: unknown): boolean {
  return isInt(x) && x >= 0;
}
function isIntOrNull(x: unknown): boolean {
  return x === null || isInt(x);
}
function isFiniteNum(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}
/** 백엔드가 만든 수치 자리 — 유한수이거나 유한수 아님을 적은 문자열. */
function isRecordedNum(x: unknown): boolean {
  return (
    isFiniteNum(x) || (typeof x === "string" && NON_FINITE_STRINGS.includes(x))
  );
}
function isStrOrNull(x: unknown): boolean {
  return x === null || typeof x === "string";
}
function hasKeys(o: Obj, keys: readonly string[]): boolean {
  return keys.every((k) => k in o);
}

function validSelection(
  s: unknown,
  winnerOk: (w: unknown) => boolean = (w) =>
    WINNER_RULES.includes(w as string | null),
  rtSources: readonly string[] = RT_SOURCES,
): boolean {
  if (s === null) return true;
  if (!isObj(s)) return false;
  if (
    !hasKeys(s, [
      "rule",
      "max_components",
      "abundance_reserve",
      "rt_bin_edges",
      "abundance_source",
      "winner_rule",
      "pool",
      "excluded_no_smiles",
    ])
  )
    return false;
  if (typeof s.rule !== "string" || typeof s.abundance_source !== "string")
    return false;
  if (!isIntOrNull(s.max_components) || !isIntOrNull(s.abundance_reserve))
    return false;
  if (
    s.rt_bin_edges !== null &&
    !(Array.isArray(s.rt_bin_edges) && s.rt_bin_edges.every(isFiniteNum))
  )
    return false;
  if (!winnerOk(s.winner_rule)) return false;
  if (!Array.isArray(s.pool) || !Array.isArray(s.excluded_no_smiles))
    return false;
  const poolOk = s.pool.every(
    (p) =>
      isObj(p) &&
      isIntOrNull(p.scan_index) &&
      isIntOrNull(p.scan_rank) &&
      isRecordedNum(p.retention_time) &&
      rtSources.includes(p.rt_source as string) &&
      isRecordedNum(p.abundance),
  );
  const exOk = s.excluded_no_smiles.every(
    (e) => isObj(e) && isIntOrNull(e.scan_index) && isIntOrNull(e.scan_rank),
  );
  return poolOk && exOk;
}

function validComponent(
  c: unknown,
  rtSources: readonly string[] = RT_SOURCES,
): c is RomshInputComponent {
  return (
    isObj(c) &&
    isInt(c.position) &&
    isStrOrNull(c.compound_name) &&
    typeof c.smiles === "string" &&
    isIntOrNull(c.scan_index) &&
    isIntOrNull(c.scan_rank) &&
    isRecordedNum(c.retention_time) &&
    rtSources.includes(c.rt_source as string) &&
    isRecordedNum(c.abundance) &&
    (c.jsd_distance === null || isRecordedNum(c.jsd_distance)) &&
    REASONS.includes(c.reason as string)
  );
}

/** 백엔드 판정 규칙 §2-6 ③(형식) 과 같은 검사. ok 로 저장된 되돌림에만 쓴다. */
function validEcho(mi: unknown): boolean {
  if (!isObj(mi) || mi.echo_version !== 1 || !hasKeys(mi, ECHO_KEYS))
    return false;
  const comps = mi.components;
  if (!Array.isArray(comps) || comps.length < 1 || comps.length > 8)
    return false;
  const compsOk = comps.every(
    (c, i) =>
      isObj(c) &&
      hasKeys(c, ECHO_COMPONENT_KEYS) &&
      c.position === i + 1 &&
      typeof c.smiles === "string" &&
      isFiniteNum(c.retention_time) &&
      isFiniteNum(c.abundance) &&
      typeof c.hydrocarbon_rule === "boolean" &&
      typeof c.truncated === "boolean" &&
      isNonNegInt(c.carbon_tokens) &&
      isNonNegInt(c.hetero_tokens) &&
      isNonNegInt(c.unknown_tokens),
  );
  if (!compsOk) return false;
  if (
    typeof mi.env_name !== "string" ||
    typeof mi.torch_version !== "string" ||
    !isFiniteNum(mi.rt_span_p95) ||
    typeof mi.env_name_known !== "boolean" ||
    !isNonNegInt(mi.min_carbon_tokens) ||
    !isNonNegInt(mi.max_mols) ||
    !isInt(mi.env_id)
  )
    return false;
  const meta = mi.meta;
  if (!Array.isArray(meta) || meta.length !== ECHO_META_NAMES.length)
    return false;
  const metaOk = meta.every(
    (m, i) => isObj(m) && m.name === ECHO_META_NAMES[i] && isFiniteNum(m.value),
  );
  if (!metaOk) return false;
  if (!["auto", "request"].includes(mi.fuel_proxy_source as string))
    return false;
  // 계산 방식·가중 방식은 AI 가 성분에서 계산했을 때(auto)만 값이 있다. 요청값을
  // 그대로 쓴 경우(request) 둘 다 null — 백엔드 판정 규칙과 같다(기획 §2-6 ③).
  if (mi.fuel_proxy_source === "auto") {
    if (typeof mi.fuel_proxy_method !== "string") return false;
    if (!["abundance", "uniform"].includes(mi.fuel_proxy_basis as string))
      return false;
  } else if (mi.fuel_proxy_method !== null || mi.fuel_proxy_basis !== null) {
    return false;
  }
  if (!["checkpoint", "default"].includes(mi.threshold_source as string))
    return false;
  const sha = (v: unknown) =>
    v === null || (typeof v === "string" && /^[0-9a-f]{64}$/.test(v));
  return sha(mi.model_sha256) && sha(mi.vocab_sha256);
}

/** 13키 기록 전체 검사(§3-4 #7 판별). */
export function isRomshInputV1(r: unknown): r is RomshInputV1 {
  if (!isRecordEnvelope(r, "romsh_input_v1")) return false;
  if (!validSelection(r.selection)) return false;
  return (
    Array.isArray(r.components) && r.components.every((c) => validComponent(c))
  );
}

/**
 * schema·selection·components 를 뺀 13키 공통 검사(v1 과 같은 규칙). v2 의 실패
 * 단계(select·build)는 이 검사만으로 상태를 정한다 — 문구에는 실패 코드만 쓴다.
 */
function isRecordEnvelope(r: unknown, schema: string): r is Obj {
  if (!isObj(r) || !hasKeys(r, RECORD_KEYS)) return false;
  if (r.schema !== schema || r.record_error !== null) return false;
  if (!OUTCOMES.includes(r.outcome as string)) return false;
  if (!Array.isArray(r.components)) return false;
  if (
    r.request !== null &&
    !(
      isObj(r.request) &&
      hasKeys(r.request, ["sample_id", "env_name", "fuel_proxy"])
    )
  )
    return false;
  if (
    r.ai_response !== null &&
    !(
      isObj(r.ai_response) &&
      isStrOrNull(r.ai_response.engine) &&
      isStrOrNull(r.ai_response.request_id)
    )
  )
    return false;
  if (!RECEIVED.includes(r.model_received_status as string)) return false;
  if (!isStrOrNull(r.model_received_note)) return false;
  if (
    r.model_received_status === "ok"
      ? !validEcho(r.model_received)
      : r.model_received !== null
  )
    return false;
  if (r.model_output !== null && !isObj(r.model_output)) return false;
  if (
    r.failure !== null &&
    !(
      isObj(r.failure) &&
      STAGES.includes(r.failure.stage as string) &&
      typeof r.failure.code === "string" &&
      isStrOrNull(r.failure.message)
    )
  )
    return false;
  return true;
}

// ---------------------------------------------------------------------------
// romsh_input_v2 (PLAN 2-9d)
// ---------------------------------------------------------------------------

/** 유한수 비율 칸 — 문자열(NaN 등)을 받지 않는다. */
function isFraction(x: unknown): boolean {
  return isFiniteNum(x) && x >= 0 && x <= 1;
}

/** pool·성분 행의 TIC 연결 값 — 단일 RT·양의 높이만(계약 3·4). */
function validHeightRow(p: unknown): boolean {
  return (
    isObj(p) &&
    isFiniteNum(p.tic_height) &&
    p.tic_height > 0 &&
    isFiniteNum(p.tic_rt) &&
    isNonNegInt(p.tic_point_index) &&
    isFiniteNum(p.retention_time) &&
    p.rt_source === HEIGHT_RT_SOURCE &&
    isFiniteNum(p.abundance) &&
    p.abundance >= 0
  );
}

/** 계산에 쓴 MS·TIC 메타데이터 해시와 RT 연결 허용 오차는 필수다. */
function validProvenance(src: unknown): boolean {
  if (
    !isObj(src) ||
    !hasKeys(src, ["ms_data_sha256", "tic_data_sha256", "tic_rt_tolerance_min"])
  )
    return false;
  return Object.entries(src).every(([k, v]) => {
    if (k.endsWith("_sha256"))
      return v === null || (typeof v === "string" && /^[0-9a-f]{64}$/.test(v));
    if (k.includes("tolerance")) return isFiniteNum(v) && v >= 0;
    return true;
  });
}

/** 알려진 출처(TIC 높이 비율) 기록의 엄격 검사 — 표를 그리는 상태에만. */
function validHeightRecord(r: Obj, s: Obj): boolean {
  if (
    s.rule !== HEIGHT_RULE ||
    s.winner_rule !== HEIGHT_WINNER_RULE ||
    s.definition_status !== HEIGHT_DEFINITION_STATUS
  )
    return false;
  if (!hasKeys(s, ["normalization_sum", "source", "excluded"])) return false;
  if (!validProvenance(s.source)) return false;
  if (
    !Array.isArray(s.excluded) ||
    !s.excluded.every(
      (e) =>
        isObj(e) &&
        isIntOrNull(e.scan_index) &&
        isIntOrNull(e.scan_rank) &&
        typeof e.reason === "string",
    )
  )
    return false;
  if (!(s.pool as unknown[]).every(validHeightRow)) return false;
  const comps = r.components as Obj[];
  if (comps.length === 0) {
    return (
      s.normalization_sum === null ||
      (isFiniteNum(s.normalization_sum) && s.normalization_sum > 0)
    );
  }
  if (!isFiniteNum(s.normalization_sum) || s.normalization_sum <= 0)
    return false;
  // 보낸 성분이 있으면 계산에 쓴 MS·TIC 해시가 있고 허용 오차가 계약값이다.
  const src = s.source as Obj;
  if (
    typeof src.ms_data_sha256 !== "string" ||
    typeof src.tic_data_sha256 !== "string" ||
    src.tic_rt_tolerance_min !== TIC_RT_TOLERANCE_MIN
  )
    return false;
  if (
    !comps.every(
      (c) => validHeightRow(c) && isFraction(c.abundance) && c.abundance !== 0,
    )
  )
    return false;
  // 모델에 보낸 성분의 비율 합은 1(계약 7). 값은 기록대로 보이고, 여기서는 형식만 본다.
  const sum = comps.reduce((a, c) => a + (c.abundance as number), 0);
  return Math.abs(sum - 1) <= FRACTION_SUM_TOLERANCE;
}

/**
 * v2 기록 전체 검사. 알려진 출처(tic_height_fraction)면 TIC 연결 값·출처 기록·
 * 비율까지 엄격히 보고, 모르는 출처는 v1 과 같은 형식만 본다(중립 표시).
 * abundance_source 가 없으면 추측하지 않고 거절한다.
 */
export function isRomshInputV2(r: unknown): r is RomshInputV2 {
  if (!isRecordEnvelope(r, "romsh_input_v2")) return false;
  const s = r.selection;
  if (!validSelection(s, (w) => isStrOrNull(w), RT_SOURCES_V2)) return false;
  if (
    !(r.components as unknown[]).every((c) => validComponent(c, RT_SOURCES_V2))
  )
    return false;
  if (isObj(s) && s.abundance_source === HEIGHT_SOURCE)
    return validHeightRecord(r, s);
  return true;
}

export type RomshInputSource = "match_score" | "tic_height" | "neutral";

/**
 * 저장된 기록의 입력값 출처 — 문구·칸은 이것으로만 고른다(지금 서버 설정 무관).
 * v1 은 당시 매칭 점수 기록이다(selection 이 없는 v1 도 그 시절 기록). 그 밖의
 * 출처는 매칭 점수라고도 TIC 라고도 부르지 않는다.
 */
export function romshInputSource(record: RomshInputRecord): RomshInputSource {
  const src = record.selection?.abundance_source;
  if (record.schema === "romsh_input_v1")
    return record.selection === null || src === "match_score"
      ? "match_score"
      : "neutral";
  return src === HEIGHT_SOURCE ? "tic_height" : "neutral";
}

// ---------------------------------------------------------------------------
// 상태 판별 (§3-4)
// ---------------------------------------------------------------------------

export type RomshInputState =
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9select"
  | "9build"
  | "9circuit"
  | "10"
  | "11a"
  | "11b"
  | "12absent"
  | "12invalid"
  | "13"
  | "14"
  | "15";

export interface RomshInputView {
  state: RomshInputState;
  /** 표 위 상태 문구. 없으면 null(#3·#8 은 아무것도 그리지 않는다, #14 는 문구 없음). */
  message: string | null;
  /** 검사를 통과한 기록 — 표를 그리는 상태에서만. */
  record: RomshInputRecord | null;
  showTable: boolean;
  /** 규칙 칸·요약값(모델 쪽 값)을 보이는지 — #14 만. */
  showModelSide: boolean;
  /** 카드에 모델 판정이 있는 상태(#12~#14) — 각주 1 끝 문장. */
  hasModelDecision: boolean;
  /** EvidenceCard 줄. null 이면 줄 없음. */
  evidenceLine: string | null;
}

function view(
  state: RomshInputState,
  message: string | null,
  evidenceLine: string | null,
  extra: Partial<RomshInputView> = {},
): RomshInputView {
  return {
    state,
    message,
    record: null,
    showTable: false,
    showModelSide: false,
    hasModelDecision: false,
    evidenceLine,
    ...extra,
  };
}

function schemaLabel(r: unknown): string {
  return isObj(r) && typeof r.schema === "string" ? r.schema : TEXT.none;
}

function unreadable(r: unknown, state: "7" | "15" = "7"): RomshInputView {
  const s = schemaLabel(r);
  return view(state, STATE_TEXT.unreadable(s), EV_TEXT.unreadable(s));
}

/**
 * 증거와 RoMSH 상태로 입력 기록의 표시 상태를 정한다. 위에서부터 먼저 걸리는
 * 하나(§3-4). 플래그(#1)·시연 시나리오 모드(#2)는 부모가 판단한다.
 *
 * @param romshStatus 분석의 romsh_status. EvidenceCard 처럼 모르면 생략한다.
 */
export function classifyRomshInput(
  evidence: AnalysisEvidence | null | undefined,
  romshStatus?: string | null,
): RomshInputView {
  if (romshStatus === "not_applicable_no_ms") return view("3", null, null);
  if (!evidence) return view("4", STATE_TEXT.noSnapshot, null);
  const params = evidence.parameters;
  if (!params || !("romsh_input" in params)) {
    return view("5", STATE_TEXT.noRecord, EV_TEXT.noRecord);
  }
  const raw: unknown = params.romsh_input;
  if (
    !isObj(raw) ||
    !(KNOWN_ROMSH_INPUT_SCHEMAS as readonly unknown[]).includes(raw.schema)
  ) {
    return unreadable(raw);
  }
  // #6 은 필수 키 검사보다 먼저 — #16 기록은 키가 셋뿐이다.
  const re = raw.record_error;
  if (
    isObj(re) &&
    typeof re.code === "string" &&
    (typeof re.message === "string" || re.message === null)
  ) {
    return view(
      "6",
      STATE_TEXT.recordError(re.code),
      EV_TEXT.recordError(re.code),
    );
  }
  // v2 의 선정·요청 조립 단계 실패(TIC 결측 등)는 성분 표가 없다 — 실패 코드만
  // 보이므로 13키 공통 검사로 상태를 정한다(pool 의 TIC 값이 비어 있어도).
  // 선택 단계 멈춤 중 건너뜀(no_winners·no_smiles)만 기존처럼 그리지 않고,
  // TIC 결측·단일 RT 없음 같은 실패는 코드를 보인다(계약: 명시적으로 실패).
  if (
    raw.schema === "romsh_input_v2" &&
    isRecordEnvelope(raw, "romsh_input_v2") &&
    raw.outcome === "not_sent" &&
    isObj(raw.failure)
  ) {
    const { stage, code } = raw.failure as { stage: string; code: string };
    if (stage === "select") {
      if (SELECT_SKIP_CODES.includes(code))
        return view("8", null, EV_TEXT.selectStopped);
      return view(
        "9select",
        HEIGHT_TEXT.selectFailed(code),
        HEIGHT_TEXT.evidenceSelectFailed(code),
      );
    }
    if (stage === "build")
      return view("9build", STATE_TEXT.build(code), EV_TEXT.notSent(code));
  }
  if (!isRomshInputV1(raw) && !isRomshInputV2(raw)) return unreadable(raw);

  const r: RomshInputRecord = raw;
  const n = r.components.length;
  const table = (
    state: RomshInputState,
    message: string | null,
    line: string,
    extra: Partial<RomshInputView> = {},
  ) => view(state, message, line, { record: r, showTable: true, ...extra });
  const f = r.failure;

  if (r.outcome === "not_sent" && f?.stage === "select") {
    return view("8", null, EV_TEXT.selectStopped);
  }
  if (r.outcome === "not_sent" && f?.stage === "build") {
    return view("9build", STATE_TEXT.build(f.code), EV_TEXT.notSent(f.code));
  }
  if (r.outcome === "not_sent" && f?.stage === "circuit") {
    return table("9circuit", STATE_TEXT.circuit, EV_TEXT.notSent(f.code));
  }
  if (r.outcome === "transport_error" && f) {
    return table(
      "10",
      STATE_TEXT.transport(f.code),
      EV_TEXT.transport(n, f.code),
    );
  }
  if (r.outcome === "sent" && f?.stage === "client_parse") {
    return table("11a", STATE_TEXT.clientParse, EV_TEXT.clientParse(n));
  }
  if (
    r.outcome === "sent" &&
    f &&
    (f.stage === "ai" || f.stage === "ai_http")
  ) {
    const why =
      f.stage === "ai_http"
        ? STATE_TEXT.noModelResultWhy.ai_http
        : STATE_TEXT.noModelResultWhy[f.code];
    if (why === undefined) {
      return table(
        "11b",
        STATE_TEXT.unknownStatus,
        EV_TEXT.noModelResult(n, EV_TEXT.unknownStatusWhy),
      );
    }
    return table(
      "11b",
      STATE_TEXT.noModelResult(why),
      EV_TEXT.noModelResult(n, why),
    );
  }
  if (r.outcome === "sent" && f === null) {
    const s = r.model_received_status;
    const decided = { hasModelDecision: true };
    if (s === "absent" || s === "error") {
      return table(
        "12absent",
        STATE_TEXT.echoAbsent,
        EV_TEXT.echoAbsent(n),
        decided,
      );
    }
    if (s === "invalid") {
      return table(
        "12invalid",
        STATE_TEXT.echoInvalid,
        EV_TEXT.echoInvalid(n),
        decided,
      );
    }
    if (s === "mismatch") {
      return table(
        "13",
        STATE_TEXT.echoMismatch,
        EV_TEXT.echoMismatch(n),
        decided,
      );
    }
    if (s === "ok" && r.selection !== null) {
      const version = r.schema.replace(/^romsh_input_/, "");
      const source = romshInputSource(r);
      const okLine =
        source === "tic_height"
          ? HEIGHT_TEXT.evidenceOk
          : source === "neutral"
            ? NEUTRAL_TEXT.evidenceOk
            : EV_TEXT.ok;
      return table(
        "14",
        null,
        okLine(n, r.selection.pool.length, r.selection.rule, version),
        { ...decided, showModelSide: true },
      );
    }
  }
  return unreadable(r, "15");
}

/** EvidenceCard 줄 — 기록만으로 판별한다(romsh_status 를 모른다). */
export function romshInputEvidenceLine(
  evidence: AnalysisEvidence | null | undefined,
): string | null {
  return classifyRomshInput(evidence).evidenceLine;
}

// ---------------------------------------------------------------------------
// 표 행 (§3-2)
// ---------------------------------------------------------------------------

export interface RomshInputRow {
  position: number;
  compoundName: string;
  smiles: string;
  rtText: string;
  rtMissing: boolean;
  observation: string;
  jsdText: string;
  reasonText: string;
  ruleText: string;
  /** 같은 관찰 괄호: ┌ │ └ 또는 "". */
  bracket: string;
  /** 같은 SMILES 가 들어간 횟수(1 이면 표식 없음). */
  dupCount: number;
  /**
   * TIC 신호 비율(기록된 abundance 를 백분율로만 바꾼 글자) — 알려진 TIC 높이
   * 출처 기록에서만. 그 밖에는 null(칸을 그리지 않는다).
   */
  signalText: string | null;
  /** 연결된 후보 행 — 없으면 누를 수 없다. */
  linkedFeatureId: string | null;
}

function fmt3(v: number | string | null): string {
  if (v === null) return TEXT.none;
  return typeof v === "number" ? v.toFixed(3) : v;
}

/** 기록된 비율을 백분율 글자로만 — 다시 계산하지 않는다. */
function fmtPercent(v: number | string): string {
  return typeof v === "number" ? `${(v * 100).toFixed(1)}%` : v;
}

/** 출처별 선정 경로 문구. v1 매칭 점수 기록은 당시 문구 그대로. */
export function reasonTextsFor(
  source: RomshInputSource,
): Record<string, string> {
  if (source === "tic_height") return HEIGHT_TEXT.reason;
  if (source === "neutral") return NEUTRAL_TEXT.reason;
  return TEXT.reason;
}

/**
 * 기록 성분을 표 행으로. 연결은 (scan_index, scan_rank) 쌍 일치(null 끼리도
 * 일치)이고 정확히 한 후보 행과 맞을 때만 잇는다. 관찰 번호는 연결된 후보 행의
 * peak_no 를 옮긴다 — scan_index 로 번호를 만들지 않는다.
 */
export function buildRomshInputRows(
  record: RomshInputRecord,
  features: readonly AnalysisFeature[],
  showModelSide: boolean,
): RomshInputRow[] {
  const comps: (RomshInputComponent | RomshInputComponentV2)[] = [
    ...record.components,
  ].sort((a, b) => a.position - b.position);
  const source = romshInputSource(record);
  const reasons = reasonTextsFor(source);
  const smilesCount = new Map<string, number>();
  for (const c of comps)
    smilesCount.set(c.smiles, (smilesCount.get(c.smiles) ?? 0) + 1);
  const echo = showModelSide ? record.model_received : null;

  const rows = comps.map((c): RomshInputRow => {
    const hits = features.filter(
      (f) =>
        (f.scan_index ?? null) === c.scan_index &&
        (f.scan_rank ?? null) === c.scan_rank,
    );
    const linked = hits.length === 1 ? hits[0] : null;
    const rtMissing = c.rt_source === "missing_sent_as_zero";
    const echoComp = echo?.components[c.position - 1];
    return {
      position: c.position,
      compoundName: c.compound_name ?? TEXT.none,
      smiles: c.smiles,
      rtText: rtMissing ? TEXT.none : fmt3(c.retention_time),
      rtMissing,
      observation:
        linked && c.scan_index !== null
          ? TEXT.observation(linked.peak_no)
          : TEXT.none,
      jsdText: fmt3(c.jsd_distance),
      reasonText: reasons[c.reason] ?? TEXT.none,
      ruleText: echoComp
        ? echoComp.hydrocarbon_rule
          ? TEXT.ruleMet
          : TEXT.ruleNotMet
        : TEXT.none,
      bracket: "",
      dupCount: smilesCount.get(c.smiles) ?? 1,
      signalText: source === "tic_height" ? fmtPercent(c.abundance) : null,
      linkedFeatureId: linked ? linked.feature_id : null,
    };
  });

  // 같은 관찰 괄호 — scan_index 가 null 이 아닌 이웃 행끼리만
  for (let i = 0; i < comps.length; i++) {
    const si = comps[i].scan_index;
    if (si === null) continue;
    const prevSame = i > 0 && comps[i - 1].scan_index === si;
    const nextSame = i < comps.length - 1 && comps[i + 1].scan_index === si;
    if (!prevSame && !nextSame) continue;
    rows[i].bracket = !prevSame ? "┌" : !nextSame ? "└" : "│";
  }
  return rows;
}

/** 입력 RT 가 모두 0 인지(부제 변형). */
export function allInputRtZero(record: RomshInputRecord): boolean {
  return (
    record.components.length > 0 &&
    record.components.every((c) => c.retention_time === 0)
  );
}

/** 요약값 줄 — 되돌림 meta 원값(소수 셋째 자리)과 env_id. #14 에서만. */
export function summaryValues(
  record: RomshInputRecord,
): { name: string; value: string }[] | null {
  const echo = record.model_received;
  if (!echo) return null;
  return [
    ...echo.meta.map((m) => ({ name: m.name, value: m.value.toFixed(3) })),
    { name: "env_id", value: String(echo.env_id) },
  ];
}
